import { useCallback, useEffect, useRef, useState } from 'react';
import { crm } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';

interface Props {
  dealId: string;
  dealTitle: string;
  /** Stufe, in der der Deal gelandet ist — z.B. „Verloren“ */
  stageName?: string;
  notes: string | null;
  /**
   * true, wenn das Feld direkt nach dem Verschieben aufgegangen ist. Dann ist
   * „ohne Notiz“ ein legitimer Ausgang und keine abgebrochene Bearbeitung.
   */
  frischVerloren?: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Das Notizfeld zu einem Deal — der Absagegrund.
 *
 * Es geht direkt beim Verschieben in eine Verloren-Stufe auf, weil der Grund
 * genau dann bekannt ist und eine Minute später niemand mehr nachträgt. Deshalb
 * ist es auch überspringbar: ein Pflichtfeld an dieser Stelle würde nur dazu
 * führen, dass irgendetwas hineingeschrieben wird.
 */
export function LostDealNotesDialog({
  dealId,
  dealTitle,
  stageName,
  notes,
  frischVerloren = false,
  onClose,
  onSaved,
}: Props) {
  const [text, setText] = useState(notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const feldRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    feldRef.current?.focus();
  }, []);

  const speichern = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await crm.pipelines.setDealNotes(dealId, text.trim() || null);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  }, [dealId, text, onSaved]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (busy) return;
      if (e.key === 'Escape') onClose();
      // Strg/Cmd + Enter speichert — die Eingabetaste allein braucht das Feld
      // für Absätze.
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void speichern();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose, speichern]);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-center overflow-y-auto bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Notiz zum Deal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void speichern();
        }}
        className="my-auto w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg"
      >
        <h2 className="text-lg font-semibold">
          {frischVerloren ? `Warum kam „${dealTitle}“ nicht zustande?` : 'Notiz zum Deal'}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {frischVerloren
            ? `Der Deal steht jetzt auf „${stageName ?? 'Verloren'}“. Der Grund hilft beim nächsten Anlauf — festhalten, solange er frisch ist.`
            : dealTitle}
        </p>

        <textarea
          ref={feldRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          placeholder="Kein Budget in diesem Schuljahr, im Frühjahr neu fragen. Ansprechpartnerin war offen, Entscheidung lag beim Träger."
          className="mt-4 w-full resize-y rounded-md border border-input bg-card px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          disabled={busy}
        />

        {error && (
          <p className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            {frischVerloren ? 'Ohne Notiz' : 'Abbrechen'}
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Speichere …' : 'Notiz speichern'}
          </Button>
        </div>
      </form>
    </div>
  );
}
